import React from 'react';
import { FormattedMessage } from 'react-intl';
import PlanCode from 'types/PlanCode';
import useLanguageRegionCode from 'hooks/useLanguageRegionCode';
import CurrencyCode from 'types/CurrencyCode';
import FormattedPercent from 'components/FormattedPercent';
import * as styles from './styles/styles.module.scss';
import * as baseStyles from '../../../../styles/baseStyles.module.scss';
import * as buttonStyles from '../../buttons/styles/styles.module.scss';
import PlanTypeType from 'types/PlanTypeType';
import Key from 'types/Key';

const CurrencyFormatter = require('currency-formatter');

type Props = {
  plan: PlanTypeType;
  onClick: (event?: React.MouseEvent) => void;
  isActive?: boolean;
  currencyCode: CurrencyCode;
  isCouponApplied: boolean;
  staticCard?: boolean;
  extraClasses?: string;
  verifiedCoupon?: {
    discountedPricePerYear: number;
    discountPercentage: number;
  };
};

const PlanTypeCard = ({ plan, onClick, isActive = false, currencyCode, isCouponApplied, verifiedCoupon, staticCard, extraClasses }: Props) => {
  const languageRegionCode = useLanguageRegionCode();

  const formatCurrency = (number: number | string) => CurrencyFormatter.format(number, { code: currencyCode });

  const formatterNoDecimals = new Intl.NumberFormat(languageRegionCode, {
    style: 'currency',
    currency: currencyCode,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  });

  const enterKeyHandler = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === Key.Enter) {
      onClick();
    }
  };

  let priceTotal: number | string = plan.priceTotal;
  let priceMonth: number | string = plan.priceMonth;
  let showDiscount = plan.showDiscount;
  let discountAmount: string | JSX.Element = plan.discountAmount;

  if (isCouponApplied && verifiedCoupon && plan.allowCoupon) {
    showDiscount = true;
    discountAmount = (
      <FormattedMessage
        defaultMessage="{percent} off"
        values={{
          percent: <FormattedPercent value={verifiedCoupon.discountPercentage / 100} />
        }}
      />
    );
    priceTotal = verifiedCoupon.discountedPricePerYear;
    priceMonth = verifiedCoupon.discountedPricePerYear / 12;
  } else if (plan.discountOverride) {
    showDiscount = true;
    discountAmount = plan.discountOverrideAmount;
  } else if (plan.planCode === PlanCode.ThreeYear) {
    // need to always render a "discount" for the 3 year plan
    discountAmount = <FormattedMessage defaultMessage="Save {percent}" values={{ percent: <FormattedPercent value={0.33} /> }} />;
  }

  const perMonth = formatCurrency(priceMonth);
  const total = formatCurrency(priceTotal);

  const planDetails = [
    {
      billedAt: (
        <FormattedMessage
          defaultMessage="Then {perMonth}/month - billed annually at {total}. Pay {trialCost} today."
          values={{ perMonth, total, trialCost: formatterNoDecimals.format(0) }}
        />
      ),
      label: <FormattedMessage defaultMessage="First 7 days free" />,
      planCode: PlanCode.SevenDayTrial
    },
    {
      billedAt: <FormattedMessage defaultMessage="{perMonth}/month - billed once at {total}" values={{ perMonth, total }} />,
      label: <FormattedMessage defaultMessage="Annual Plan" />,
      planCode: PlanCode.AnnualOnce
    },
    {
      billedAt: <FormattedMessage defaultMessage="{perMonth}/month - billed annually at {total}" values={{ perMonth, total }} />,
      label: <FormattedMessage defaultMessage="Annual Plan" />,
      planCode: PlanCode.Annual
    },
    {
      billedAt: <FormattedMessage defaultMessage="{perMonth}/month - billed once at {total}" values={{ perMonth, total }} />,
      label: <FormattedMessage defaultMessage="Three-Year Plan" />,
      planCode: PlanCode.ThreeYear
    },
    {
      billedAt: <FormattedMessage defaultMessage="{perMonth}/month - billed monthly at {total}" values={{ perMonth, total }} />,
      label: <FormattedMessage defaultMessage="Monthly Plan" />,
      planCode: PlanCode.Monthly
    }
  ];

  const planDetail = planDetails.find(_ => _.planCode === plan.planCode);

  if (!planDetail) {
    throw new Error(`No plan detail found for plan code ${plan.planCode}`);
  }

  let discount;
  let billedAtLang = planDetail.billedAt;

  if (showDiscount) {
    // billing language is slightly different when discounting first year
    discount = <div className={styles.discount}>{discountAmount}</div>;

    if (plan.planCode === PlanCode.Annual) {
      billedAtLang = <FormattedMessage defaultMessage="{perMonth}/month - 1st year billed at {total}" values={{ perMonth, total }} />;
    }
  }

  return (
    <div
      tabIndex={staticCard ? -1 : 0}
      onClick={onClick}
      onKeyPress={enterKeyHandler}
      role="button"
      className={`${isActive ? styles.active : ''} ${!onClick ? styles.staticCard : ''} ${buttonStyles.button} ${styles.card} ${extraClasses}`}
    >
      <div className={styles.contents}>
        <div className={styles.label}>
          <div className={styles.plan}>{planDetail.label}</div>
          {discount}
        </div>
        <div className={(baseStyles.row, styles.details)}>{billedAtLang}</div>
      </div>
    </div>
  );
};

export default PlanTypeCard;
